// CreditsOverlay — public/-Stack-Variante (Drift-Map D4, 2026-05-21).
//
// Portiert aus app/-Stack (PublicViewerPage.tsx Z. 43-89 + 334-338).
// Slide-Level-Asset-Credits Overlay am unteren Rand des Viewers.
// Progress-getriggert: aktuelle Slide-Position → Credit-Text aus
// post.data.data.slides[N].credits.
//
// Architektur-Unterschied app/ vs. public/:
//   app/:    Hook useSlideCredits(post, rootRef) — DOM-scroll-listener
//            auf scroll-container.
//   public/: Reine getter-Funktion getCurrentSlideCredit(post, progress)
//            — derived state, kein scroll-listener nötig weil ViewerChrome
//            schon progress (0-100) als state hält.
//
// Z-Index: Z.credits=50 (unterhalb StoryFooter Z=60, oberhalb Toast Z=40).
//
// Renderstrategie:
// - Wrapper-Div mit pointerEvents:none → Scroll-Events passieren durch
// - Inner span optional pointerEvents:auto wenn Unsplash-Links existieren
// - Light vs. Dark Color-Scheme via credit.light Flag
// - Unsplash-Pattern-Detection (`Foto: NAME / Unsplash`) → 2 verlinkte Spans

import type { PostJson } from "@scrolly/post-types";
import { Z } from "../../lib/z-index";

// Unsplash API guideline: all attribution links back to Unsplash must carry
// utm_source + utm_medium=referral + utm_campaign=api-credit.
const UNSPLASH_UTM = "?utm_source=scrolly&utm_medium=referral&utm_campaign=api-credit";

/** Slide-Credit Sub-Shape aus post.data.data.slides[N].credits (PPI-Convention). */
export interface SlideCredit {
  text: string;
  light: boolean;
  photographerUrl?: string;
}

/**
 * Derives current slide's credit from post + progress (0..1 or 0..100).
 * Returns null if:
 *   - post is null/undefined
 *   - slides[idx].credits is missing or text is empty
 */
export function getCurrentSlideCredit(
  post: PostJson | null | undefined,
  progress: number,
): SlideCredit | null {
  if (!post) return null;
  const slides = (
    post.data as { data?: { slides?: Array<{ id?: string; credits?: SlideCredit }> } } | undefined
  )?.data?.slides;
  if (!Array.isArray(slides) || slides.length === 0) return null;

  // Normalize: support 0..1 and 0..100 inputs (ViewerChrome uses 0..100).
  const normalized = progress > 1 ? progress / 100 : progress;
  const clamped = Math.max(0, Math.min(1, normalized));

  const isBloom = slides.some((s) => s?.id === 'slide-bloom');
  if (isBloom) {
    const bloomIdx = slides.findIndex((s) => s?.id === 'slide-bloom');
    const bloomCredit = slides[bloomIdx]?.credits;
    // Unsplash/bg-image credits live on slide-bloom — not on cover slide-1.
    if (bloomCredit?.text?.trim() && clamped > 0.03) {
      return {
        text: bloomCredit.text,
        light: bloomCredit.light ?? true,
        photographerUrl: bloomCredit.photographerUrl,
      };
    }
    return null;
  }

  const idx = Math.min(
    Math.floor(clamped * slides.length),
    slides.length - 1,
  );
  const slideCredits = slides[idx]?.credits;
  if (!slideCredits?.text) return null;
  return {
    text: slideCredits.text,
    light: slideCredits.light ?? true,
    photographerUrl: slideCredits.photographerUrl,
  };
}

interface CreditsOverlayProps {
  post?: PostJson | null;
  /** Progress 0..1 (or 0..100, auto-detected). ViewerChrome benutzt 0..100. */
  progress: number;
  /** Skip render in OG-Mode (Headless-Screenshot). */
  isOg?: boolean;
}

export function CreditsOverlay({ post, progress, isOg }: CreditsOverlayProps) {
  if (isOg) return null;
  const credit = getCurrentSlideCredit(post, progress);
  if (!credit) return null;

  const color = credit.light ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)";
  const linkColor = credit.light
    ? "rgba(255,255,255,0.85)"
    : "rgba(0,0,0,0.75)";

  // Unsplash-Pattern: "Foto: NAME / Unsplash" → 2 verlinkte Spans
  const match = credit.photographerUrl
    ? credit.text.match(/^(Foto:\s*)(.+?)(\s*\/\s*)(Unsplash)$/)
    : null;

  return (
    <div
      style={{
        position: "absolute",
        bottom: 6,
        left: 0,
        right: 0,
        zIndex: Z.credits,
        pointerEvents: "none",
        textAlign: "center",
        fontSize: 12,
        fontFamily:
          "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {match ? (
        <span
          style={{
            color,
            display: "inline-block",
            pointerEvents: "auto",
          }}
        >
          {match[1]}
          <a
            href={`${credit.photographerUrl}${UNSPLASH_UTM}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: linkColor,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {match[2]}
          </a>
          {match[3]}
          <a
            href={`https://unsplash.com${UNSPLASH_UTM}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: linkColor,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {match[4]}
          </a>
        </span>
      ) : (
        <span style={{ color }}>{credit.text}</span>
      )}
    </div>
  );
}

export default CreditsOverlay;
